/** @format */
import clsx from 'clsx'
import React, {useEffect, useRef} from 'react'

const OTP_LENGTH = 6
export function OtpInput({
  allowedCharacters = '^[0-9]*$',
  onChange,
  disabled = false,
}) {
  const inputsRef = useRef([])
  // Last value reported per cell, so a 2-char value can be told apart:
  // "prev + typed" / "typed + prev" (one new char, caret anywhere) versus an
  // OS one-time-code autofill dropping the whole code into one cell.
  const prevValues = useRef(Array(OTP_LENGTH).fill(''))

  useEffect(() => {
    inputsRef.current[0].focus()
  }, [])

  const updateOnChange = () => {
    prevValues.current = inputsRef.current.map(input => input.value)
    onChange(prevValues.current.join(''))
  }

  // Spread a multi-character value (paste, OS one-time-code autofill) over
  // the cells from `start`, keeping only allowed characters so "418 027\n"
  // still fills. Cells before `start` are left alone.
  const fillCells = (raw, start = 0) => {
    const chars = Array.from(raw).filter(c => c.match(allowedCharacters))
    if (chars.length > 0) {
      // Cells past the pasted length are cleared too, so a short paste over
      // an older code can't produce a mixed 6-digit value.
      for (let i = start; i < OTP_LENGTH; i++) {
        inputsRef.current[i].value =
          i - start < chars.length ? chars[i - start] : ''
      }
      const last = Math.min(start + chars.length, OTP_LENGTH) - 1
      const next = inputsRef.current[last].nextElementSibling
      ;(next !== null ? next : inputsRef.current[last]).focus()
    }
    updateOnChange()
  }

  const handleOnChange = e => {
    const {value} = e.target
    if (value.length > 1) {
      const index = inputsRef.current.indexOf(e.target)
      const prev = prevValues.current[index] || ''
      if (value.length === 2 && prev && value.startsWith(prev)) {
        e.target.value = value[1] // typed after the existing char
      } else if (value.length === 2 && prev && value.endsWith(prev)) {
        e.target.value = value[0] // typed before it (caret at 0)
      } else {
        // Spread from the cell that received it: autofill only lands in cell
        // 0, and an IME committing two digits into cell 3 must not wipe 0-2.
        e.target.value = ''
        fillCells(value, index)
        return
      }
    }
    if (e.target.value.match(allowedCharacters)) {
      if (e.target.nextElementSibling !== null) {
        e.target.nextElementSibling.focus()
      }
    } else {
      e.target.value = ''
    }
    updateOnChange()
  }

  const handleOnKeyDown = e => {
    const {key} = e
    const target = e.target
    if (key === 'Backspace') {
      if (target.value === '' && target.previousElementSibling !== null) {
        target.previousElementSibling.value = ''
        target.previousElementSibling.focus()
        e.preventDefault()
      } else {
        target.value = ''
      }
      updateOnChange()
    }
  }

  const handleOnFocus = e => {
    e.target.select()
  }

  const handleOnPaste = e => {
    // 'text/plain' is the canonical type; the legacy 'Text' alias is empty in
    // some environments (jsdom included).
    fillCells(e.clipboardData.getData('text/plain'))
    e.preventDefault()
  }

  const inputs = []
  for (let i = 0; i < OTP_LENGTH; i++) {
    inputs.push(
      <input
        key={i}
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        onFocus={handleOnFocus}
        onPaste={handleOnPaste}
        disabled={disabled}
        type="text"
        inputMode="numeric"
        autoComplete={i === 0 ? 'one-time-code' : 'off'}
        ref={el => (inputsRef.current[i] = el)}
        className={clsx({
          'text-uppercase mr-1 w-10 shrink rounded border border-2 p-2 text-center text-xl text-secondary focus:ring-0 sm:mr-4 sm:w-12 sm:text-3xl': true,
          'border-green-400': Boolean(
            inputsRef.current[i] && inputsRef.current[i].value,
          ),
          'focus:border-gray-400': !Boolean(
            inputsRef.current[i] && inputsRef.current[i].value,
          ),
        })}
      />,
    )
  }

  return <div className="flex justify-center px-0 py-2 sm:p-8">{inputs}</div>
}
